import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ClipboardCheck,
  Plus,
  Trash2,
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  BookOpen,
  Hash,
  Calendar,
  PlusCircle,
  Lock,
  X, // Added missing import
} from "lucide-react";

type Grade = "A1" | "B2" | "B3" | "C4" | "C5" | "C6" | "D7" | "E8" | "F9";

type SubjectRow = { subject: string; grade: Grade | "" };

type ResultForm = {
  exam_type: "waec" | "neco" | "nabteb";
  exam_year: string;
  exam_number?: string;
  subjects: SubjectRow[];
  result_file?: string; // existing uploaded scan path (edit / resume)
};

/** Prelim/IJMB grades are free-form (they don't use the WAEC A1–F9 scale). */
type PrelimSubjectRow = { subject: string; grade: string };

/** Optional Prelim / IJMB / other state-level result (upgrade applications only). */
type PrelimForm = {
  exam_type: "prelim" | "ijmb" | "others";
  other_exam_name?: string;
  exam_year: string;
  institution: string;
  exam_number?: string;
  subjects: PrelimSubjectRow[];
  result_file?: string; // existing uploaded path (edit / resume)
};

export type Step3OLevelInitial = {
  results: Array<{
    exam_type: "waec" | "neco" | "nabteb";
    exam_year: string;
    exam_number?: string;
    subjects: Record<string, string>;
    result_file?: string;
  }>;
  prelim_results?: Array<{
    exam_type: "prelim" | "ijmb" | "others";
    other_exam_name?: string;
    exam_year: string;
    institution: string;
    exam_number?: string;
    subjects: Record<string, string>;
    result_file?: string;
  }>;
};

const STORAGE_BASE = "https://api.niqsng.org/storage";
const storageUrl = (p?: string | null) =>
  !p ? null : p.startsWith("http") ? p : `${STORAGE_BASE}/${p.replace(/^\//, "")}`;

export default function Step3OLevel({
  onSubmit,
  busy,
  onBack,
  initialValues,
  readOnly,
  showPrelim = false,
}: {
  busy: boolean;
  onBack: () => void;
  readOnly?: boolean;
  /** Show the optional Prelim / IJMB section (grade-upgrade applications only). */
  showPrelim?: boolean;
  onSubmit: (
    results: any[],
    resultFilesByIndex: Record<number, File | null>,
    prelimResults?: any[],
    prelimFilesByIndex?: Record<number, File | null>,
  ) => Promise<void>;
  initialValues?: Step3OLevelInitial | null;
}) {
  const [localError, setLocalError] = useState<string | null>(null);
  // New scanned-copy uploads, keyed by result index (compulsory per result).
  const [filesByIndex, setFilesByIndex] = useState<Record<number, File | null>>({});
  // Optional Prelim / IJMB records and their (compulsory) result uploads.
  const [prelims, setPrelims] = useState<PrelimForm[]>([]);
  const [prelimFiles, setPrelimFiles] = useState<Record<number, File | null>>({});

  // Keep in step with the backend cap (scanFileRules: max 10 MB) so an oversized
  // scan is caught here with a clear message instead of only after a failed
  // upload. Returns the file if acceptable, otherwise null (and shows the error).
  const MAX_FILE_MB = 10;
  const acceptScan = (file: File | null): File | null => {
    if (!file) return null;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setLocalError(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The maximum is ${MAX_FILE_MB} MB — please upload a smaller or compressed scan.`,
      );
      return null;
    }
    setLocalError(null);
    return file;
  };

  const defaultSubjects: SubjectRow[] = useMemo(
    () => [
      { subject: "Mathematics", grade: "" },
      { subject: "English", grade: "" },
      { subject: "Physics", grade: "" },
      { subject: "", grade: "" },
      { subject: "", grade: "" },
    ],
    [],
  );

  const [results, setResults] = useState<ResultForm[]>([
    {
      exam_type: "waec",
      exam_year: "",
      exam_number: "",
      subjects: defaultSubjects,
    },
  ]);

  const hydratedRef = useRef(false);

  const normalize = (v: string) => v.trim().toLowerCase();

  const mapSubjectsObjToRows = (obj: Record<string, string>): SubjectRow[] => {
    const rows: SubjectRow[] = [];
    const math = Object.keys(obj).find((k) => normalize(k) === "mathematics");
    const eng = Object.keys(obj).find((k) => normalize(k) === "english");
    const phy = Object.keys(obj).find((k) => normalize(k) === "physics");

    rows.push({
      subject: "Mathematics",
      grade: (math ? (obj[math] as any) : "") || "",
    });
    rows.push({
      subject: "English",
      grade: (eng ? (obj[eng] as any) : "") || "",
    });
    rows.push({
      subject: "Physics",
      grade: (phy ? (obj[phy] as any) : "") || "",
    });

    Object.entries(obj).forEach(([subject, grade]) => {
      const key = normalize(subject);
      if (key === "mathematics" || key === "english" || key === "physics")
        return;
      rows.push({ subject, grade: (String(grade).toUpperCase() as any) || "" });
    });

    while (rows.length < 5) rows.push({ subject: "", grade: "" });
    return rows;
  };

  useEffect(() => {
    if (!initialValues || hydratedRef.current) return;
    const incoming = initialValues.results || [];
    if (incoming.length === 0) return;
    hydratedRef.current = true;

    setResults(
      incoming.map((r) => ({
        exam_type: r.exam_type || "waec",
        exam_year: String(r.exam_year || ""),
        exam_number: r.exam_number || "",
        subjects: mapSubjectsObjToRows(r.subjects || {}),
        result_file: r.result_file || undefined,
      })),
    );

    const incomingPrelims = initialValues.prelim_results || [];
    if (incomingPrelims.length) {
      setPrelims(
        incomingPrelims.map((p) => ({
          exam_type: p.exam_type || "prelim",
          other_exam_name: p.other_exam_name || "",
          exam_year: String(p.exam_year || ""),
          institution: p.institution || "",
          exam_number: p.exam_number || "",
          subjects: Object.entries(p.subjects || {}).map(([subject, grade]) => ({
            subject,
            grade: String(grade || ""),
          })),
          result_file: p.result_file || undefined,
        })),
      );
    }
  }, [initialValues]);

  const setResult = (i: number, patch: Partial<ResultForm>) => {
    setResults((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const setSubjectRow = (
    ri: number,
    si: number,
    patch: Partial<SubjectRow>,
  ) => {
    setResults((s) =>
      s.map((r, idx) => {
        if (idx !== ri) return r;
        const next = r.subjects.map((x, j) =>
          j === si ? { ...x, ...patch } : x,
        );
        return { ...r, subjects: next };
      }),
    );
  };

  const addSubjectRow = (ri: number) => {
    setResults((s) =>
      s.map((r, idx) =>
        idx === ri
          ? { ...r, subjects: [...r.subjects, { subject: "", grade: "" }] }
          : r,
      ),
    );
  };

  const removeSubjectRow = (ri: number, si: number) => {
    setResults((s) =>
      s.map((r, idx) => {
        if (idx !== ri || r.subjects.length <= 5) return r;
        return { ...r, subjects: r.subjects.filter((_, j) => j !== si) };
      }),
    );
  };

  /* ── Optional Prelim / IJMB helpers ─────────────────────────────────── */
  const setPrelim = (i: number, patch: Partial<PrelimForm>) =>
    setPrelims((s) => s.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const addPrelim = () =>
    setPrelims((s) => [
      ...s,
      {
        exam_type: "prelim",
        other_exam_name: "",
        exam_year: "",
        institution: "",
        exam_number: "",
        subjects: [
          { subject: "", grade: "" },
          { subject: "", grade: "" },
        ],
      },
    ]);

  const removePrelim = (i: number) => {
    setPrelims((s) => s.filter((_, idx) => idx !== i));
    setPrelimFiles((f) => {
      const next = { ...f };
      delete next[i];
      return next;
    });
  };

  const setPrelimSubject = (pi: number, si: number, patch: Partial<PrelimSubjectRow>) =>
    setPrelims((s) =>
      s.map((p, idx) =>
        idx !== pi
          ? p
          : { ...p, subjects: p.subjects.map((x, j) => (j === si ? { ...x, ...patch } : x)) },
      ),
    );

  const addPrelimSubject = (pi: number) =>
    setPrelims((s) =>
      s.map((p, idx) =>
        idx === pi ? { ...p, subjects: [...p.subjects, { subject: "", grade: "" }] } : p,
      ),
    );

  const removePrelimSubject = (pi: number, si: number) =>
    setPrelims((s) =>
      s.map((p, idx) =>
        idx !== pi || p.subjects.length <= 1
          ? p
          : { ...p, subjects: p.subjects.filter((_, j) => j !== si) },
      ),
    );

  const toPrelimPayload = (p: PrelimForm) => {
    const obj: Record<string, string> = {};
    p.subjects.forEach((x) => {
      const s = x.subject.trim();
      const g = String(x.grade || "").trim();
      if (s && g) obj[s] = g;
    });
    return {
      exam_type: p.exam_type,
      other_exam_name: p.exam_type === "others" ? (p.other_exam_name || "").trim() : undefined,
      exam_year: p.exam_year.trim(),
      institution: p.institution.trim(),
      exam_number: p.exam_number?.trim() || undefined,
      subjects: obj,
      result_file: p.result_file || undefined,
    };
  };

  const validatePrelim = (p: PrelimForm, i: number) => {
    const n = i + 1;
    if (p.exam_type === "others" && !(p.other_exam_name || "").trim())
      return `Prelim/IJMB #${n}: state the name of the exam.`;
    if (!p.exam_year.trim()) return `Prelim/IJMB #${n}: year is required.`;
    if (!p.institution.trim()) return `Prelim/IJMB #${n}: institution is required.`;
    const filled = p.subjects.filter((x) => x.subject.trim() && String(x.grade || "").trim());
    if (filled.length < 1) return `Prelim/IJMB #${n}: add at least one subject and grade.`;
    if (!prelimFiles[i] && !p.result_file)
      return `Prelim/IJMB #${n}: the result must be uploaded.`;
    return null;
  };

  const validateOne = (r: ResultForm, i: number) => {
    if (!r.exam_year) return `Result #${i + 1}: Exam year is required.`;
    if (!r.exam_number?.trim())
      return `Result #${i + 1}: Exam number is required.`;
    const filled = r.subjects.filter((x) => x.subject.trim() && x.grade);
    if (filled.length < 5)
      return `Result #${i + 1}: Provide at least 5 subjects with grades.`;
    const keys = filled.map((x) => normalize(x.subject));
    if (!keys.includes("english"))
      return `Result #${i + 1}: English Language is compulsory.`;
    if (!keys.includes("mathematics"))
      return `Result #${i + 1}: Mathematics is compulsory.`;
    if (!keys.includes("physics"))
      return `Result #${i + 1}: Physics is compulsory.`;
    if (new Set(keys).size !== keys.length)
      return `Result #${i + 1}: Remove duplicate subjects.`;
    return null;
  };

  const toBackendPayload = (r: ResultForm) => {
    const obj: Record<string, string> = {};
    r.subjects.forEach((x) => {
      const s = x.subject.trim();
      const g = String(x.grade || "")
        .trim()
        .toUpperCase();
      if (s && g) obj[s] = g;
    });
    return {
      exam_type: r.exam_type,
      exam_year: r.exam_year.trim(),
      exam_number: r.exam_number?.trim() || undefined,
      subjects: obj,
      result_file: r.result_file || undefined, // existing path, preserved on edit
    };
  };

  return (
    <form
      className="space-y-8 pb-10"
      onSubmit={async (e) => {
        e.preventDefault();
        setLocalError(null);
        for (let i = 0; i < results.length; i++) {
          const msg = validateOne(results[i], i);
          if (msg) {
            setLocalError(msg);
            return;
          }
          // A scanned copy is compulsory: either a new upload or a previously
          // uploaded file must be present.
          if (!filesByIndex[i] && !results[i].result_file) {
            setLocalError(`Result #${i + 1}: A scanned copy of the result is required.`);
            return;
          }
        }
        // Prelim/IJMB is optional — but any record added must be complete.
        if (showPrelim) {
          for (let i = 0; i < prelims.length; i++) {
            const msg = validatePrelim(prelims[i], i);
            if (msg) {
              setLocalError(msg);
              return;
            }
          }
        }
        await onSubmit(
          results.map(toBackendPayload),
          filesByIndex,
          showPrelim ? prelims.map(toPrelimPayload) : [],
          showPrelim ? prelimFiles : {},
        );
      }}
    >
      <div className="border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-900 rounded-xl">
            <ClipboardCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">O-Level Results</h2>
            <p className="text-sm text-gray-500">
              Add your WAEC/NECO results. English, Mathematics and Physics are
              compulsory. Maximum 3 sittings.
            </p>
          </div>
        </div>
      </div>

      {localError && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-700 text-sm font-medium">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {localError}
        </div>
      )}

      <div className="space-y-10">
        {results.map((r, i) => (
          <div
            key={i}
            className="relative bg-white border border-gray-200 rounded-3xl p-6 transition-all hover:border-gray-300"
          >
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="px-4 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest">
                  Exam Sitting {i + 1}
                </div>
                {results.length > 1 && (
                  <button
                    type="button"
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    onClick={() =>
                      setResults((x) => x.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2">
                  <BookOpen className="w-3 h-3" /> Exam Type
                </label>
                <select
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 transition-all"
                  value={r.exam_type}
                  onChange={(e) =>
                    setResult(i, { exam_type: e.target.value as any })
                  }
                  disabled={busy}
                >
                  <option value="waec">WAEC (WASSCE)</option>
                  <option value="neco">NECO (SSCE)</option>
                  <option value="nabteb">NABTEB</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2">
                  <Calendar className="w-3 h-3" /> Exam Year{" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 transition-all"
                  value={r.exam_year}
                  onChange={(e) => setResult(i, { exam_year: e.target.value })}
                  disabled={busy}
                >
                  <option value="">Select Year</option>
                  {Array.from(
                    { length: new Date().getFullYear() - 1979 },
                    (_, k) => new Date().getFullYear() - k,
                  ).map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <Field
                label="Exam Number"
                icon={<Hash className="w-3 h-3" />}
                value={r.exam_number || ""}
                onChange={(v: string) => setResult(i, { exam_number: v })}
                disabled={busy}
                placeholder="e.g. 4123456789"
                required
              />
            </div>

            {/* Compulsory scanned copy of the result */}
            <div className="rounded-2xl p-5 border border-gray-100 bg-gray-50/50 space-y-2 mb-5">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest block">
                Scanned copy of result <span className="text-red-500">*</span>
              </label>
              {!readOnly && (
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={busy}
                  onChange={(e) => {
                    const f = acceptScan(e.target.files?.[0] ?? null);
                    if (!f) { e.target.value = ""; return; }
                    setFilesByIndex((s) => ({ ...s, [i]: f }));
                  }}
                  className="w-full text-xs"
                />
              )}
              {filesByIndex[i] ? (
                <p className="text-[11px] text-emerald-600 font-medium">
                  Selected: {filesByIndex[i]?.name}
                </p>
              ) : storageUrl(r.result_file) ? (
                <a
                  href={storageUrl(r.result_file)!}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-indigo-600 hover:underline font-medium"
                >
                  View uploaded copy
                </a>
              ) : (
                <p className="text-[11px] text-gray-400">
                  Upload a scan or clear photo (PDF/JPG/PNG, up to {MAX_FILE_MB} MB). Required for each result.
                </p>
              )}
            </div>

            <div className="bg-gray-50/50 rounded-2xl p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
                <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                  Subject Grades Breakdown
                </h4>
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-xs font-bold text-gray-900 hover:text-gray-600 transition-colors"
                  onClick={() => addSubjectRow(i)}
                  disabled={busy}
                >
                  <PlusCircle className="w-4 h-4" /> Add Subject
                </button>
              </div>

              <div className="space-y-3">
                {r.subjects.map((row, si) => {
                  const isLocked =
                    normalize(row.subject) === "english" ||
                    normalize(row.subject) === "mathematics" ||
                    normalize(row.subject) === "physics";
                  return (
                    <div
                      key={si}
                      className="grid md:grid-cols-12 gap-4 items-center animate-in fade-in duration-300"
                    >
                      <div className="md:col-span-7 relative">
                        <input
                          className={`w-full p-2.5 border rounded-xl text-sm transition-all outline-none 
                            ${
                              isLocked
                                ? "bg-white border-gray-100 text-gray-400 cursor-not-allowed font-medium"
                                : "bg-white border-gray-200 focus:ring-2 focus:ring-gray-100"
                            }`}
                          value={row.subject}
                          placeholder="e.g. Biology"
                          onChange={(e) =>
                            setSubjectRow(i, si, { subject: e.target.value })
                          }
                          disabled={busy || isLocked}
                        />
                        {isLocked && (
                          <Lock className="absolute right-3 top-3 w-3 h-3 text-gray-300" />
                        )}
                      </div>

                      <div className="md:col-span-4">
                        <select
                          className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-gray-100"
                          value={row.grade}
                          onChange={(e) =>
                            setSubjectRow(i, si, {
                              grade: (e.target.value as any) || "",
                            })
                          }
                          disabled={busy}
                        >
                          <option value="">Grade</option>
                          {[
                            "A1",
                            "B2",
                            "B3",
                            "C4",
                            "C5",
                            "C6",
                            "D7",
                            "E8",
                            "F9",
                          ].map((g) => (
                            <option key={g} value={g}>
                              {g}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-1 flex justify-center">
                        {r.subjects.length > 5 && !isLocked && (
                          <button
                            type="button"
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                            onClick={() => removeSubjectRow(i, si)}
                            disabled={busy}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {results.length < 3 && (
        <button
          type="button"
          className="flex items-center gap-2 px-5 py-2.5 text-gray-900 border-2 border-dashed border-gray-300 rounded-2xl text-xs font-bold hover:border-gray-900 transition-all"
          onClick={() =>
            setResults((s) => [
              ...s,
              {
                exam_type: "waec",
                exam_year: "",
                exam_number: "",
                subjects: defaultSubjects,
              },
            ])
          }
          disabled={busy}
        >
          <Plus className="w-4 h-4" />
          Combine Another Sitting
          <span className="ml-1 text-gray-400 font-normal">
            ({results.length}/3)
          </span>
        </button>
      )}

      {/* ── Optional: Prelim / IJMB / other state-level results ───────────── */}
      {showPrelim && (
        <div className="mt-8 rounded-3xl border-2 border-indigo-200 bg-indigo-50/50 p-6 space-y-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-indigo-600 rounded-2xl shrink-0">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                  Prelim / IJMB Results
                  <span className="px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-[10px] font-black uppercase tracking-widest text-indigo-600">
                    Optional
                  </span>
                </h3>
                <p className="text-xs text-gray-600 mt-1 max-w-xl leading-relaxed">
                  Do you have a <strong>Prelim</strong>, <strong>IJMB</strong> or other state-level
                  result? Add it here. Skip this if it doesn't apply — but any record you add must be
                  complete, with the result uploaded.
                </p>
              </div>
            </div>
            {!readOnly && (
              <button
                type="button"
                onClick={addPrelim}
                disabled={busy}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all shadow-sm shrink-0"
              >
                <Plus className="w-4 h-4" /> Add Result
              </button>
            )}
          </div>

          {prelims.length === 0 && !readOnly && (
            <button
              type="button"
              onClick={addPrelim}
              disabled={busy}
              className="w-full py-7 rounded-2xl border-2 border-dashed border-indigo-200 bg-white/70 text-center hover:border-indigo-400 hover:bg-white transition-all"
            >
              <p className="text-sm font-bold text-gray-700">No Prelim / IJMB result added</p>
              <p className="text-xs text-gray-500 mt-1">
                Click to add one — or leave this blank and continue.
              </p>
            </button>
          )}

          {prelims.map((p, pi) => (
            <div key={pi} className="p-5 border border-gray-100 rounded-2xl bg-gray-50/60 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Record {pi + 1}
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removePrelim(pi)}
                    disabled={busy}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Exam <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={p.exam_type}
                    onChange={(e) => setPrelim(pi, { exam_type: e.target.value as any })}
                    disabled={busy || readOnly}
                    className="mt-1 w-full p-3 bg-white border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5"
                  >
                    <option value="prelim">Prelim</option>
                    <option value="ijmb">IJMB</option>
                    <option value="others">Others</option>
                  </select>
                </div>

                {p.exam_type === "others" && (
                  <Field
                    label="State the exam"
                    value={p.other_exam_name || ""}
                    onChange={(v: string) => setPrelim(pi, { other_exam_name: v })}
                    disabled={busy || readOnly}
                    placeholder="e.g. JUPEB"
                    required
                  />
                )}

                <Field
                  label="Year"
                  value={p.exam_year}
                  onChange={(v: string) => setPrelim(pi, { exam_year: v })}
                  disabled={busy || readOnly}
                  placeholder="e.g. 2019"
                  required
                />
                <Field
                  label="Institution"
                  value={p.institution}
                  onChange={(v: string) => setPrelim(pi, { institution: v })}
                  disabled={busy || readOnly}
                  placeholder="Institution that issued the result"
                  required
                />
                <Field
                  label="Exam Number"
                  value={p.exam_number || ""}
                  onChange={(v: string) => setPrelim(pi, { exam_number: v })}
                  disabled={busy || readOnly}
                  placeholder="Candidate / exam number"
                />
              </div>

              {/* Subjects */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                  <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Subjects
                  </h4>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => addPrelimSubject(pi)}
                      disabled={busy}
                      className="flex items-center gap-1.5 text-xs font-bold text-gray-900 hover:text-gray-600"
                    >
                      <PlusCircle className="w-4 h-4" /> Add Subject
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {p.subjects.map((s, si) => (
                    <div key={si} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        className="col-span-7 p-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5"
                        placeholder="Subject"
                        value={s.subject}
                        disabled={busy || readOnly}
                        onChange={(e) => setPrelimSubject(pi, si, { subject: e.target.value })}
                      />
                      <input
                        className="col-span-4 p-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5"
                        placeholder="Grade"
                        value={s.grade}
                        disabled={busy || readOnly}
                        onChange={(e) => setPrelimSubject(pi, si, { grade: e.target.value })}
                      />
                      <div className="col-span-1 flex justify-center">
                        {p.subjects.length > 1 && !readOnly && (
                          <button
                            type="button"
                            onClick={() => removePrelimSubject(pi, si)}
                            disabled={busy}
                            className="p-1.5 text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Result upload — compulsory for any record added */}
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Upload result <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={busy || readOnly}
                  onChange={(e) => {
                    const picked = acceptScan(e.target.files?.[0] ?? null);
                    if (!picked) { e.target.value = ""; return; }
                    setPrelimFiles((f) => ({ ...f, [pi]: picked }));
                  }}
                  className="mt-1 block w-full text-xs text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-gray-900 file:text-white hover:file:bg-gray-800"
                />
                {!prelimFiles[pi] && p.result_file && (
                  <p className="mt-1 text-[11px] text-green-600 font-medium">
                    A result is already uploaded — choose a file only to replace it.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="flex justify-between items-center pt-8 border-t border-gray-100">
          <button
            type="button"
            disabled={busy}
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-xl font-bold text-sm text-gray-600 hover:bg-gray-50 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <button
            disabled={busy}
            className="group flex items-center gap-3 px-8 py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-200"
          >
            {busy ? "Processing..." : "Save & Continue"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      )}
    </form>
  );
}

// Added interface for explicit typing
interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  icon?: React.ReactNode;
  required?: boolean;
}

function Field({
  label,
  value,
  onChange,
  disabled,
  placeholder,
  icon,
  required,
}: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-2">
        {icon} {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        required={required}
        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 transition-all"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
    </div>
  );
}
